import {Component, Input} from "@angular/core";
import {MatIconModule} from "@angular/material/icon";
import {MatMenuModule} from "@angular/material/menu";
import {MatButtonModule} from "@angular/material/button";
import {RouterLink} from "@angular/router";
import Big from "big.js";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {FundValuationWeightingDivergence} from "../../models/fund-valuation-weighting-divergence";

/**
 * WI 1085: an info affordance next to a "various" weighting label. Clicking opens a popover that lists
 * the fund valuations whose weighting differs from the settings default — fund name, valuation
 * description, and the probability for the given scenario — plus a deep-link to the weighting settings.
 */
@Component({
    selector: "valumize-weighting-divergence-popover",
    standalone: true,
    imports: [MatIconModule, MatMenuModule, MatButtonModule, RouterLink],
    templateUrl: "./weighting-divergence-popover.component.html",
    styleUrl: "./weighting-divergence-popover.component.scss",
})
export class WeightingDivergencePopoverComponent {
    @Input() divergences: FundValuationWeightingDivergence[] = [];
    /** Whether users may override the org weighting default; drives the settings deep-link target. */
    @Input() userOverridable = false;

    /**
     * The settings scope to deep-link to: "my" (My Settings / General) when users may override the org
     * default, else "org" (Organization defaults / General) where an admin sets it.
     */
    get settingsScope(): "my" | "org" {
        return this.userOverridable ? "my" : "org";
    }

    /**
     * The fund valuation's full Low/Base/High weighting as whole percents "L / B / H". The whole triple
     * is shown (not just the open scenario's component) so the deviation is always visible — two funds
     * can share one scenario's probability yet still differ overall.
     */
    weightingLabel(divergence: FundValuationWeightingDivergence): string {
        const pct = (fraction: number) => new Big(DecimalFormatPipe.transformFractionToPercent(fraction) ?? 0).round(0).toString();
        return `${pct(divergence.lowWeight)} / ${pct(divergence.baseWeight)} / ${pct(divergence.highWeight)}`;
    }
}
